<template>
  <section>
    <div id="fp" class="modal-card" style="width: auto">
      <header class="modal-card-head footerSourceModal-head">
        <p class="modal-card-title footerSourceModal-title">FAQ</p>
      </header>
      <section id="PrintBody" ref="PrintBody" class="modal-card-body footerSourceModal">
        <div v-html="$t('faq.faq_fp')"></div>
        <hr>
        <div v-html="$t('faq.faq_general')"></div>
      </section>
      <footer class="modal-card-foot footerSourceModal-foot">
        <b-button class="button" @click="print()">{{$t('faq.print_text')}}</b-button>
        <b-button class="button" @click="$parent.close()">{{$t('faq.close_text')}}</b-button>
      </footer>
    </div>
  </section>
</template>

<script>
import Vue from "vue";
import { Modal} from "buefy";
import Printd from "printd";
import VueI18n from "vue-i18n";

Vue.use(VueI18n);
Vue.use(Modal);
// Create VueI18n instance with options
export const i18n = new VueI18n({
  locale: 'en',
})

export default {
  i18n: {
    locale: 'en',
    messages: {
      en: {
        faq: {
          faq_fp: `
            <h2 class="is-size-4">Financial Performance Data</h2>
            <h3 class="is-size-5">Where did you get this information from?</h3>
            <p>
              We downloaded the raw data from the <a href="https://open.canada.ca/data/en/dataset/f559526c-40f9-4c5d-bced-0f7a41f278b9" target="_blank">Government of Canada 
              Open Data Portal</a>, which is publicly available. This program contains information licensed under the 
              <a href="https://open.canada.ca/en/open-government-licence-canada" target="_blank">Open Government Licence – Canada</a>.
            </p>
            <h3 class="is-size-5">How can I access this information in a different format?</h3>
            <p>
              You can download the raw data in another format from the <a href="https://open.canada.ca/data/en/dataset/f559526c-40f9-4c5d-bced-0f7a41f278b9" target="_blank">Government of Canada Open Data Portal</a>, 
              which is publicly available. This program contains information licensed under the <a href="https://open.canada.ca/en/open-government-licence-canada" target="_blank">Open Government Licence – Canada</a>.
            </p>
            <h3 class="is-size-5">How was this information collected?</h3>
            <p>
              Financial Performance Data is based on tax return information from all businesses in Canada reporting annual revenue of between $30,000 to $5 million. Because 
              information is collected from all businesses (i.e. a "census"), there are no sampling techniques being used.
            </p>
            <h3 class="is-size-5">How many people provided information?</h3>
            <p>
              Information was provided by the total population of businesses. The exact number depends on how many businesses exist in your chosen region and industry.
            </p>
            <h3 class="is-size-5">How often is this updated?</h3>
            <p>
              The most current information we have is for 2015. You can get the raw data for more recent year directly from the 
              <a href="https://open.canada.ca/data/en/dataset?portal_type=dataset&q=Financial+Performance+Data" target="_blank">Government of Canada Open Data Portal</a>, 
              which is publicly available.
            </p>
            <h3 class="is-size-5">I'm not sure what a term (for example: 'profitable business') means.</h3>
            <p>
              Please see the <a href="https://www.ic.gc.ca/eic/site/pp-pp.nsf/eng/h_pm00003.html" target="_blank">Financial Performance Data glossary</a> for definitions of 
              the terms used.
            </p>
            <h3 class="is-size-5">What is the difference between what I see here and what is available on the Financial Performance Data website?</h3>
            <p>
              We present information in a visual way that is quick and easy to understand. We also allow for easy comparisons between locations and/or industries.
            </p>
            <p>
              However, we only present some of the information that is available. If you are looking for more detailed information (such as breakdowns by quartiles), the 
              <a href="https://www.ic.gc.ca/eic/site/pp-pp.nsf/eng/home" target="_blank">Financial Performance Data website</a> and 
              <a href="https://open.canada.ca/data/en/dataset?portal_type=dataset&q=Financial+Performance+Data" target="_blank">Open Data</a> are better choices.
            </p>
            <h3 class="is-size-5">Why can't I find information on other financial performance measures, like debt to equity ratio?</h3>
            <p>
              We are constantly seeking to grow and improve our products. If you have a suggestion for a new type of information you'd like to see, feel free to contact us 
              with your ideas.
            </p>`,
          faq_general: `
            <h2 class="is-size-4">General</h2>
            <h3 class="is-size-5">Why are certain industries named and grouped the way they are?</h3>
            <p>
              The information presented here is based on the most recent (2017) Canadian version of the 
              <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank">North American Industry Classification System</a>. We use the 
              standard titles, definitions, and examples provided in the <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank">NAICS handbook</a>.
            </p>
            <h3 class="is-size-5">Why can't I find information for my industry and/or location?</h3>
            <p>
              The information may not exist in the combination you've chosen. For example, poppy flowers are not endemic to Canada so poppy farming is not likely to yield 
              results in any province selected.
            </p>
            <p>
              Sometimes an industry is represented by such a small number of businesses in a certain location that we can't share the data without violating the privacy of 
              those businesses. Other times, the information may be inaccurate or missing. In these cases, Statistics Canada has flagged the data as unreliable and we have 
              removed it from our collection.
            </p>
            <h3 class="is-size-5">Why am I being shown a different industry and/or location than the one I chose?</h3>
            <p>
              We always show the most detailed and specific information available. However, if information is not available for your choices, the program will show the most 
              similar industry or location for which information is available.
            </p>
            <p>
              In the case of location, this often means showing you national instead of provincial information.
            </p>
            <p>
              In the case of industry, this often means showing you information from a broader sector instead of the specific industry you chose. For example, we may not 
              have information on resorts in particular but we have information on the traveller accommodation sector (which includes resorts, hotels, motels, and motor 
              homes).
            </p>`,
          print_text: "Print",
          close_text: "Close"
        }
      },
      fr: {
        faq: {
          faq_fp: `
            <h2 class="is-size-4">Données sur la performance financière</h2>
            <h3 class="is-size-5">Où avez-vous obtenu cette information?</h3>
            <p>
              Nous avons téléchargé les données brutes du <a href="https://ouvert.canada.ca/data/fr/dataset/f559526c-40f9-4c5d-bced-0f7a41f278b9" target="_blank">Portal de 
              données ouvert du Gouvernement du Canada</a>, qui est accessible à tous. Ce programme contient de l'information sous la 
              <a href="https://ouvert.canada.ca/fr/licence-du-gouvernement-ouvert-canada" target="_blank">Licence du gouvernement ouvert – Canada</a>.
            </p>
            <h3 class="is-size-5">Comment je peux obtenir cette information sous un autre format?</h3>
            <p>
              Vous pouvez télécharger les données brutes dans un autre format directement à partir du site Web 
              <a href="https://ouvert.canada.ca/data/fr/dataset/f559526c-40f9-4c5d-bced-0f7a41f278b9" target="_blank">Portal de données ouvert du Gouvernement du Canada</a>, 
              qui est accessible à tous. Ce programme contient de l'information sous la 
              <a href="https://ouvert.canada.ca/fr/licence-du-gouvernement-ouvert-canada" target="_blank">Licence du gouvernement ouvert – Canada</a>.
            </p>
            <h3 class="is-size-5">Comment l'information a-t-elle été collecté?</h3>
            <p>
              Les Données sur la performance financière sont basés sur l'information des déclarations de revenus de toutes entreprises au Canada ayant un revenu annuel entre 
              30&nbsp;000&nbsp;$ à 5&nbsp;millions. Parceque l'information est collecté de toutes les entreprises (ex. : un recensement), il n'y a pas de techniques 
              d'échantillonnage qui sont utilisés.
            </p>
            <h3 class="is-size-5">Combien de personnes ont fourni de l'information?</h3>
            <p>
              L'information a été fourni par la population d'entreprises total. Le nombre exact dépend sur combien d'entreprises qui existent dans votre région et secteur 
              d'activité que vous avez choisi.
            </p>
            <h3 class="is-size-5">Quelle est la fréquence de mise à jour des données?</h3>
            <p>
              L'information la plus courante que nous avons est pour 2015. Vous pouvez obtenir les données brutes pour des années plus récentes directement du site Web 
              <a href="https://ouvert.canada.ca/data/fr/dataset?q=Donn%C3%A9es+sur+la+performance+financi%C3%A8re" target="_blank">Portal de données ouvert du Gouvernement du 
              Canada</a>, qui est accessible à tous.
            </p>
            <h3 class="is-size-5">Je ne suis pas certain ce qu'un terme (par exemple : 'entreprises rentables') veut dire.</h3>
            <p>
              Veuillez consulter <a href="https://www.ic.gc.ca/eic/site/pp-pp.nsf/fra/h_pm00003.html" target="_blank">Glossaire de Données sur la performance financière</a> 
              pour les définitions des termes utilisés.
            </p>
            <h3 class="is-size-5">Qu'elle est la difference entre ce quoi je trouve ici et ce qui est disponible sur le site Web Données sur la performance financière?</h3>
            <p>
              Nous présentons l'information dans un visuel qui est vite et facile à comprendre. Nous permettons aussi des comparaisons facile entre emplacements et/ou secteurs 
              d'activités.
            </p>
            <p>
              Cependant, nous présentons seulement certaine information qui sont disponible. Si vous cherchez pour de l'information plus détaillée (comme des analyses par 
              quarts), le site Web <a href="https://www.ic.gc.ca/eic/site/pp-pp.nsf/fra/accueil" target="_blank">Données sur la performance financière</a> et 
              <a href="https://ouvert.canada.ca/data/fr/dataset?q=Donn%C3%A9es+sur+la+performance+financi%C3%A8re" target="_blank">Données ouvertes</a> sont des meilleurs choix.
            </p>
            <h3 class="is-size-5">Pourquoi je ne peux trouver de l'information sur les autres mesures du rendement financier, comme le ratio d'endettement passif-avoir?</h3>
            <p>
              Nous sommes constament à la recherche de développer et d'améliorer nos produits. Si vous avez une suggestion pour un nouveau type d'information que vous voulez 
              voir, n'hésitez pas à nous contacter avec vos idées.
            </p>`,
          faq_general: `
            <h2 class="is-size-4">Générale</h2>
            <h3 class="is-size-5">Pourquoi certains secteurs d'activités sont nommés et groupés de cette façon?</h3>
            <p>
              L'information présenté ici est basé sur la version canadienne la plus récente (2017) du 
              <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">Système de classification des industries de l'Amérique du Nord</a>. 
              Nous utilisons les titres, les définitions et les exemples standards fourni dans le 
              <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">guide du SCIAN</a>.
            </p>
            <h3 class="is-size-5">Pourquoi je ne peux trouver l'information pour mon secteur d'activité et/ou l'emplacement?</h3>
            <p>
              L'information peut ne pas existé dans la combinaison que vous avez choisi. Par exemple, le coquelicot n'est pas une espèce endémique du Canada donc l'agriculture 
              du coquelicot ne produirait probablement pas des résultats dans l'une ou l'autre des provinces sélectionnées.
            </p>
            <p>
              Parfois un secteur d'activité est représenté par un faible nombre d'entreprises dans un certain emplacement que nous ne pouvons pas partager les données sans 
              faire abstraction à la protection de la vie privée de ses entrprises. En d'autre temps, l'information peut être inexacte ou manquant. Dans ces cas, Statistique 
              Canada a signalé les données comme un fonctionnement incertain et nous les avons retiré de notre collection.
            </p>
            <h3 class="is-size-5">Pourquoi je suis montré un secteur d'activité et/ou emplacement différent de ce que j'ai choisi?</h3>
            <p>
              Nous montrons toujours l'information la plus détaillé et spécifique disponible. Cependant, si l'information n'est pas disponible pour vos choix, le programme va 
              montrer le secteur d'activité ou l'emplacement le plus similaire dont l'information est disponible.
            </p>
            <p>
              Dans le cas de l'emplacement, ceci veut souvent dire qu'on vous montre l'information national au lieu de provincial.
            </p>
            <p>
              Dans le cas du secteur d'activité, ceci veut souvent dire qu'on vous montre l'information à partir d'un secteur plus générale au lieu d'un secteur d'activité 
              spécifique que vous avez choisi. Par exemple, nous n'avons peut-être pas l'information sur les centres de villégiature en particulier mais nous avons 
              l'information sur le secteur de l'hébergement des voyageurs (qui inclus centres de villégiature, hôtels, motels et maisons motorisées).
            </p>`,
          print_text: "Imprimer",
          close_text: "Fermer"
        }
      }
    }
  }, // end i18n
  props: [
    'lang',
  ],
  mounted: function() {
    this.$i18n.locale = this.toggleLang;
  },
  computed: {
    toggleLang: function() {
      return this.lang;
    }
  }, // end computed
  methods: {
    print: function() {
      const d = new Printd();
      d.print(this.$refs.PrintBody);
    }
  } // end methods
}
</script>
